import React, { useEffect, useRef, useState } from "react";

import { DECISION, MAX_CANDIDATES, isWeak } from "./matchModel";
import { TEXT } from "./editorText";

const M = TEXT.match;
const NEW_KEY = "new";

/** Where a candidate comes from, as a short tag: a runbook line cannot be edited; a learned one was taught by an engineer. */
export const sourceTag = (candidate) => {
  if (candidate.editable === false) return M.fromRunbook;
  return candidate.source === "learned" ? M.learned : "";
};

const KIND_TAG = Object.freeze({ fact: M.kindFact, symptom: M.kindSymptom });
/** What kind of node a candidate is ('Fact' / 'Symptom'); empty when the server did not say. */
export const kindTag = (candidate) => KIND_TAG[candidate && candidate.kind] || "";

/** What one option says under its text: where the candidate comes from and how strong the match is; "Create new" explains itself. */
function Meta({ candidate }) {
  if (!candidate) return <span className="le-cand-meta">{M.createNewHint}</span>;
  const tag = sourceTag(candidate);
  const kind = kindTag(candidate);
  return (
    <span className="le-cand-meta">
      {kind && <span className="le-tag" title={M.kindOf(kind.toLowerCase())}>{kind}</span>}
      {tag && <span className="le-tag le-tag--muted">{tag}</span>}
      <span>{isWeak(candidate) ? M.weak : M.strong}</span>
    </span>
  );
}

/**
 * The inline listbox under a row's control: the engineer's line as a reminder, then the top candidates as radio-like options
 * (full catalog text, source, strength) and "Create new" last. Arrow keys / Home / End move, Enter or Space picks, Esc
 * closes (and the control gets focus back). `value` is the current choice {decision, selected_id}; `onPick(decision, id)`;
 * `onClose(refocus)`.
 */
export default function CandidateList({ id, line, candidates, value, onPick, onClose }) {
  const options = [...candidates.slice(0, MAX_CANDIDATES).map((c) => ({ key: c.id, candidate: c })), { key: NEW_KEY, candidate: null }];
  const selected = options.findIndex((o) => (o.candidate
    ? value.decision === DECISION.EXISTING && value.selected_id === o.candidate.id
    : value.decision !== DECISION.EXISTING));
  const [active, setActive] = useState(Math.max(0, selected));
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.focus(); }, []);

  const pick = (option) => {
    onPick(option.candidate ? DECISION.EXISTING : DECISION.NEW, option.candidate ? option.candidate.id : null);
    onClose(true);
  };
  const move = (to) => setActive((to + options.length) % options.length);
  const onKeyDown = (event) => {
    const handled = { ArrowDown: () => move(active + 1), ArrowUp: () => move(active - 1), Home: () => move(0), End: () => move(options.length - 1),
      Enter: () => pick(options[active]), " ": () => pick(options[active]), Escape: () => onClose(true) }[event.key];
    if (!handled) return;
    event.preventDefault();
    event.stopPropagation(); // Esc closes the list, not the dialog
    handled();
  };

  return (
    <div className="le-cands-wrap">
      <p className="le-cands-head">{`${M.yourLine}: `}<strong>{line}</strong></p>
      <ul className="le-cands" role="listbox" id={id} ref={ref} tabIndex={0} aria-label={M.chooseFor(line)}
        aria-activedescendant={`${id}-${active}`} onKeyDown={onKeyDown} onBlur={() => onClose(false)}>
        {options.map((option, i) => (
          <li key={option.key} id={`${id}-${i}`} role="option" aria-selected={i === selected}
            className={`le-cand${option.candidate ? "" : " le-cand--new"}${i === active ? " is-active" : ""}${i === selected ? " is-selected" : ""}`}
            onMouseDown={(event) => event.preventDefault()} onClick={() => pick(option)}>
            <span className="le-cand-radio" aria-hidden="true" />
            <span className="le-cand-main">
              <span className="le-cand-text">{option.candidate ? option.candidate.label : M.createNew}</span>
              <Meta candidate={option.candidate} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
